// Shared page-body for all Vike scrolly viewer routes.
//
// Plan-1.9 Phase 4 STOPP 4.A.8: introduced to back the four backwards-
// compatible URL patterns (Master-Briefing v5 Z. 435) with a single
// rendering implementation:
//   - `/s/:slug`                            → urlKey=slug, mockFallback=on
//   - `/scrolly/:publicKey`                 → urlKey=publicKey
//   - `/:orgSlug/:scrollySlug`              → urlKey=`<orgSlug>/<scrollySlug>`
//   - `/scrollytelling/:orgSlug/:scrollySlug` → urlKey=`<orgSlug>/<scrollySlug>`
//
// Each Vike page passes its routeParams-derived `urlKey` plus the
// `pageData` from its own `+data.ts` resolver. The mock-fallback path
// (`SCROLLYS_BY_SLUG[slug]`) is opt-in via `mockFallbackKey` and only
// used by `/s/:slug` because the dev feed-mocks are keyed by demo-slug,
// not by publicKey or org+slug pairs.
//
// Renders identical Phase-3 features (renderMode=og, scene-anchor,
// resume-state) for all routes — the route differences are purely in
// data-resolution and URL-display, not in viewer behaviour.

import { BRANDING_DEFAULTS, type BrandingTokens } from "@scrolly/branding";
import { ViewerChrome } from "./ViewerChrome";
import { BrandingProvider } from "./BrandingProvider";
import { NotFound } from "../common/NotFound";
import { LoadError } from "../common/LoadError";
import { ScrollyErrorBoundary } from "../common/ScrollyErrorBoundary";
import { PINK } from "../../lib/tokens";
import type { CategoryId } from "../../lib/categories";
import { useCallback, useEffect, useRef } from "react";
import type { CtaAbgabe } from "@scrolly/post-types";
// Plan-N2 (2026-05-25): useViewerMode-Hook nicht mehr aufgerufen
// (Mode-Toggle wurde aus TopBar entfernt). Aber localStorage-Eintrag
// aus früheren Sessions ("scrolly_viewer_mode=immersive") muss
// einmalig gecleared werden — sonst zeigt Bene's Browser weiter
// Immersive-Mode obwohl ViewerChrome jetzt auf "phone" defaultet.
import {
  SCROLLYS_BY_SLUG,
  type MockScrolly,
} from "../../lib/mock/scrollys";
import { extractThumbnailUrl } from "../../lib/cover-thumbnail";
import { extractBackgroundImageUrl } from "../../lib/boot-hintergrund";
import { isInAppWebView } from "../../lib/in-app-webview";
import type { ResolvedScrolly } from "../../lib/api/client";
import type { ResolvedScrollyPageData } from "../../lib/api/resolved-scrolly-page-data";
import { parseSceneAnchor } from "../../lib/url/parseSceneAnchor";
import { formatScrollyDate } from "../../lib/util/format-date";
import type { PageContext } from "vike/types";
import { useViewTracking } from "../../hooks/useViewTracking";
import { useDurationTracking } from "../../hooks/useDurationTracking";
import { seitenScrollAn } from "../../lib/seiten-scroll";

/**
 * Dev-only URL-query overrides for the mock-fallback branding tokens.
 *
 * Phase 13 Commit 12 used this to produce the four smoke screenshots
 * (default / custom-color / google-font / logo-enabled) against the
 * mock scrolly route, because the end-to-end backend fixture is not
 * seeded in this environment. Active only under `import.meta.env.DEV`;
 * in production the query is ignored and BRANDING_DEFAULTS stays.
 *
 * Recognised query params:
 *   `smokeColor=%23FF00FF`      → tokens.colors.primary
 *   `smokeFont=Playfair+Display` → tokens.typography.display.family
 *   `smokeLogo=<url>`           → tokens.logo.light.url
 *   `smokeShow=1`               → tokens.showLogoInViewer
 */
export function applyDevTokenOverrides(
  base: BrandingTokens,
  search: Record<string, string> | undefined,
): BrandingTokens {
  if (!import.meta.env.DEV || !search) return base;
  const color = search["smokeColor"];
  const font = search["smokeFont"];
  const logo = search["smokeLogo"];
  const show = search["smokeShow"] === "1";
  if (!color && !font && !logo && !show) return base;
  return {
    ...base,
    colors: color
      ? { ...base.colors, primary: color, accent: color }
      : base.colors,
    typography: font
      ? {
          ...base.typography,
          display: { ...base.typography.display, family: font },
        }
      : base.typography,
    logo: logo
      ? {
          ...base.logo,
          light: { ...base.logo.light, url: logo },
        }
      : base.logo,
    showLogoInViewer: show ? true : base.showLogoInViewer,
  };
}

/**
 * Build a MockScrolly-shaped object from backend-resolved data so that
 * ViewerChrome (built around MockScrolly) can render real content without
 * a full prop rewrite. Stats/category/creator are sensible defaults — the
 * chrome reads them but they're cosmetic for the backend-driven path.
 *
 * `urlKey` is the URL-derived identifier (slug, publicKey, or composite)
 * used as the synthetic React id. `resolved.publicKey` is the real backend
 * publicKey used for Phase-3.A Resume-State storage key.
 *
 * PostJson-Konvention (CLAUDE.md): `resolved.post` is the api-client-wrapped
 * `PostJson` shape (`{ data: PostBackend }`), wrapped at the client.ts
 * boundary (see `public/src/lib/api/client.ts` fetchResolved). That means:
 *   - `resolved.post.data.title` → PostBackend.title (PostSerialized field)
 *   - `resolved.post.data.data.slides` → PresentationSerialized.slides
 * Passing `resolved.post` straight to `postJson:` keeps the renderer's
 * double-nesting invariant intact.
 */
export function synthScrollyFromResolved(
  urlKey: string,
  resolved: ResolvedScrolly,
): MockScrolly {
  const post = resolved.post.data;
  const creatorName = resolved.orgContact?.name ?? "Scrolly";
  // Sprint-1-Eyebrow-Fix: PostJson.categoryName ist Forward-Compat-Field
  // im post-types-Schema (post.ts:410), heute aber von Backend nicht
  // gesetzt. Fallback "story" rendert in CoverPage als hidden-Category
  // "STORY · A Scrolly". Bene-Entscheidung 2026-05-19 Pfad A.
  // Plan-2-Backlog: Backend-Schema-Erweiterung um categoryName persistent.
  const categoryName = (resolved.post as { categoryName?: string })
    .categoryName;
  return {
    id: urlKey,
    slug: urlKey,
    // COVER-ÜBERSCHRIFT, nicht Dateiname (Jans Fund 08.08.2026): post.title
    // ist der ARBEITSNAME aus der Team-Übersicht („Mixed Images (9 Images)");
    // die echte Überschrift pflegt die cover-meta-Spiegelung in
    // metadata.title — dieselbe Quelle, die auch der echte StartScreen
    // (packages/viewer) zeigt. Vorher zeigte das Vorschalt-Cover des
    // Voll-Readers deshalb einen anderen Titel als die Editor-Vorschau.
    // Fallback auf den Arbeitsnamen für Altbestand ohne metadata.title —
    // aber NUR für Teilen/SEO/Chrome-Texte. Das COVER nutzt coverTitle.
    title: post.metadata?.title || post.title || "Scrolly",
    // COVER-Überschrift ohne jeden Rückfall (Jans Regel 30.08.2026 am
    // Wiedergabe-Scrolly): leer heißt leer — das Cover zeigt dann nur den
    // START-Knopf statt des Dateinamens.
    coverTitle: post.metadata?.title || "",
    // #6-Fix (Laura, 2026-07-28): der Cover zeigt den Untertitel
    // (metadata.subtitle), NICHT die längere Social/OG-Beschreibung.
    subtitle: post.metadata?.subtitle || undefined,
    description: post.description || undefined,
    categoryId: (categoryName ?? "story") as CategoryId,
    creator: {
      handle: creatorName.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      name: creatorName,
      avatar: PINK,
    },
    cover: {
      palette: "journalism",
      decoration: "headlines",
    },
    // Reader-Cover (2026-06-01): echtes Pro-Scrolly-Titelbild aus dem
    // Backend-JSON (`post` = resolved.post.data = PostBackend, gleiche
    // Shape wie A's BackendScrolly.json). undefined → CoverSection nutzt
    // den konstanten dunklen Fallback. Logik gespiegelt aus Cover-Fix A.
    coverImage: extractThumbnailUrl(post),
    // Boot-Blende (Jan 02.09.2026): das EIGENE Hintergrundbild des Scrollys —
    // das Vorschaubild gehört in Kacheln/Link-Vorschauen, nicht auf den Titel.
    // Kein Hintergrundbild → keine Blende (nur der Vorhang, Regel vom 24.08.).
    bootBackgroundImage: extractBackgroundImageUrl(post),
    stats: {
      views: 0,
      duration: "",
      likes: 0,
      saves: 0,
    },
    // Sprint-1-Revision 3.5.5-Fix: Backend liefert publishedAt (optional)
    // bzw. createdAt (PostBackend-required). Fallback-Reihenfolge:
    // publishedAt → createdAt → "" (=Sub-Line wird hidden in TopBar).
    //
    // Plan-N2 Block 3 (2026-05-25 Bene): Bisher floss der ISO-String roh
    // ins MockScrolly.publishedAt (`"2026-04-06T21:48:38.657Z"`) — der
    // Colophon zeigte ihn als rohen Timestamp. Jetzt durch den schon
    // existierenden `formatScrollyDate`-Hybrid-Formatter
    // (public/src/lib/util/format-date.ts) gejagt: <30 Tage relativ
    // ("vor 3 Tagen"), sonst absolut ("6. April 2026"). MockScrolly-Type
    // Doc-Comment "Relativ-deutsch für Phase 11" hat das immer schon
    // erwartet — nur die Verdrahtung fehlte.
    publishedAt: formatScrollyDate(post.publishedAt ?? post.createdAt),
    // Roh-Wert fuer "Veroeffentlicht am TT.MM.JJJJ" im Abbinder (Jan 21.09.2026)
    publishedIso: resolved.anzeigeDatum ?? post.publishedAt ?? post.createdAt ?? null,
    featured: false,
    tags: [],
    source: "native",
    postJson: resolved.post,
    // Plan-1.9 Phase 3.A.CP3 — Resume-State-Storage-Key + scrollyVersion.
    publicKey: resolved.publicKey,
    updatedAt: resolved.updatedAt,
    // Block 2.6 P7 (2026-05-22): Legal-Links für Colophon-Abbinder.
    legalLinks: resolved.legalLinks,
    // Plan-N2 Block 2 (2026-05-25 Bene): Org-Kontakt für Colophon-Contact-
    // Block. Mirror der app/-Pipeline — Backend liefert das schon in
    // ResolvedScrolly.orgContact, wir reichen es nur durch.
    orgContact: resolved.orgContact,
    // „Nur Vollbild" des Erstellers (Jan 28.09.2026) → ViewerChrome
    nurVollbild: resolved.nurVollbild === true,
    // Org-Branding-Flag: badges.showColophon===false blendet die End-Slide
    // (Colophon) generell aus. badges kommt org-only über EffectiveBranding.
    // Default = zeigen (nur explizites false blendet aus).
    showColophon:
      ((resolved.branding as { badges?: { showColophon?: boolean } })?.badges
        ?.showColophon ?? true) !== false,
    // Org-Branding-Flag: badges.showEngagement===false blendet das Like/
    // Speichern/Teilen-Menü (SideActions + OverflowActionsMenu) generell aus —
    // z.B. WESER-KURIER (Menü verwirrt deren Leser:innen). Default = zeigen.
    showEngagement:
      ((resolved.branding as { badges?: { showEngagement?: boolean } })?.badges
        ?.showEngagement ?? true) !== false,
    // Org-Branding-Flag: badges.showLikeSave===false laesst nur Teilen stehen
    // (Like + Speichern weg). Lauras Wunsch fuer Weser-Kurier, 08.09.2026.
    showLikeSave:
      ((resolved.branding as { badges?: { showLikeSave?: boolean } })?.badges
        ?.showLikeSave ?? true) !== false,
    // Like + Speichern nur, wenn der Scrolly auf der Homepage steht (Jan 03.10.2026)
    aufPortal: resolved.aufPortal,
    // Org-Branding-Flag: badges.showScrollyBadge===false blendet das
    // "powered by scrolly"-Band + die Bold-Markenzeile im Colophon aus.
    // Nur moeglich, wenn der Server es zulaesst (Badge-Zwang: white_label
    // via Plan/Superadmin-planOverride, user-scrollys.service 27.08.2026).
    showPoweredBy:
      ((resolved.branding as { badges?: { showScrollyBadge?: boolean } })
        ?.badges?.showScrollyBadge ?? true) !== false,
    // Render-Zeit-Fonts (2026-07-20): Custom-Fonts des Autors. Der Viewer
    // nutzt sie als Fallback, wenn das eingebackene slideOptions leer ist —
    // sonst rendert die Story in der Default-Schrift statt der Marken-Schrift.
    authorFonts: resolved.authorFonts,
  };
}

export interface ScrollyViewerPageProps {
  /** Vike pageContext (from usePageContext()). */
  pageContext: PageContext;
  /** Discriminated-union result from the route's `+data.ts`. */
  pageData: ResolvedScrollyPageData | undefined;
  /** URL-derived identifier (slug, publicKey, or composite). */
  urlKey: string;
  /**
   * Optional mock-fallback key. Only `/s/:slug` sets this (slug-based feed
   * mocks). Other routes leave it undefined so the not-found path renders
   * straight away when `+data.ts` returns notFound.
   */
  mockFallbackKey?: string;
  /**
   * URL-prefix for the NotFound message ("Kein Scrolly unter <prefix>/<urlKey>").
   * Default `/s` for the legacy short-link pattern.
   */
  notFoundUrlPrefix?: string;
}

/**
 * Shared scrolly viewer page.
 *
 * Wraps the page body in a per-scrolly ErrorBoundary (A-4, Code-Review
 * 2026-05-29) so a render-time crash in the shared renderer shows a readable
 * error card instead of a white screen, and is isolated to this route. The
 * boundary resets on scrolly change (`resetKey={urlKey}`) so navigating to a
 * different scrolly recovers.
 */
export function ScrollyViewerPage(props: ScrollyViewerPageProps) {
  return (
    <ScrollyErrorBoundary resetKey={props.urlKey} context={props.urlKey}>
      <ScrollyViewerPageBody {...props} />
    </ScrollyErrorBoundary>
  );
}

/**
 * Shared scrolly viewer page-body.
 *
 * Dispatches on the discriminated union from `pageData`:
 *   - success → render ViewerChrome with a synthetic MockScrolly built
 *               from the resolved backend data
 *   - notFound → optional mock-fallback (only `/s/:slug`), then <NotFound/>
 *   - error → <LoadError/> with retry
 *
 * Includes Phase-3 features (renderMode=og, scene-anchor) for all routes.
 */
function ScrollyViewerPageBody({
  pageContext,
  pageData,
  urlKey,
  mockFallbackKey,
  notFoundUrlPrefix = "/s",
}: ScrollyViewerPageProps) {
  // Plan-N2 (2026-05-25): Cleanup alten Mode-Toggle-State.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem("scrolly_viewer_mode");
      // URL-Param auch aufräumen falls vorhanden
      const url = new URL(window.location.href);
      if (url.searchParams.has("mode")) {
        url.searchParams.delete("mode");
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      }
    } catch {
      // Storage disabled — no-op
    }
  }, []);

  // Variante D (Vorbedingungs-Track STOPP D CP2): ?renderMode=og blendet
  // im ViewerChrome alle UI-Chrome-Elemente aus, damit der Headless-Browser
  // im OG-Image-Backend ein sauberes Vollbild rendern kann.
  //
  // 2026-05-29: Plus ?embedded=1 — Subset-Strip nur für iframe-Overlay
  // aus portal (ViewerOverlay-Component). Strippt TopBar (X-Button +
  // 3-Punkte-Menu — kollidieren mit Overlay-eigenem X), SideActions und
  // OverflowActionsMenu (Engagement-Aktionen kommen vom Parent-Overlay).
  // chromeOverlays (CompanyLogo/Credits/Scroll-Indicator) und TitleOverlay
  // bleiben sichtbar — sind Inhalt, nicht Engagement-Chrome.
  //
  // SSR-verträglich via pageContext.urlParsed.search statt
  // window.location.search.
  const renderMode: "og" | "embedded" | undefined =
    pageContext.urlParsed?.search?.["renderMode"] === "og"
      ? "og"
      : pageContext.urlParsed?.search?.["embedded"] === "1"
        ? "embedded"
        : undefined;

  // VORSCHAU-KENNZEICHEN (27.09.2026, Benedikts Befund im CTA-Stufe-2-Papier):
  // ?preview=1 = diese Seite wird als Vorschau eingebettet, nicht von einem
  // echten Leser gelesen. Dann zählt sie weder als Aufruf noch als Verweildauer,
  // und VOTE/RATING melden keine Abgaben. Jede Stelle, die diese Seite als
  // Vorschau in ein iframe lädt, MUSS den Parameter setzen.
  const istVorschau = pageContext.urlParsed?.search?.["preview"] === "1";

  // Overlay-Stufe-B (2026-06-10): ?chrome=minimal — das Portal-Overlay
  // rendert Like/Save/Share selbst (same-origin, authentifiziert), der
  // iframe zeigt deshalb KEIN eigenes Engagement-Chrome (SideActions +
  // embedded-OverflowActionsMenu). Realität vor Stufe B: der Polish-
  // Subset-Revert (29.05.) zeigte SideActions AUCH embedded — daher die
  // Desktop-Überlappung im Overlay. Standalone-Links (ohne Param)
  // behalten ihr in-viewer-Engagement + Batch-2-Auth-Hydration.
  const chromeMinimal =
    pageContext.urlParsed?.search?.["chrome"] === "minimal";

  // Handy-Ansicht erzwingen (Jan 15.09.2026): `?ansicht=handy` kommt vom
  // Portal-Kachel-Klick (portal/src/lib/viewer-navigation.ts). Damit starten
  // auch Pro-Editor-Scrollys am Desktop im Handy-Rahmen mit Umschalter —
  // Direkt-/Share-Links ohne Parameter bleiben bei der Regel vom 11.09.
  // (Pro-Editor = Vollbild). SSR und Client lesen dieselbe Query.
  const handyAnsicht = pageContext.urlParsed?.search?.["ansicht"] === "handy";

  // Seiten-Scroll (Benedikt 28.09.2026): am Handy scrollt die ganze Seite
  // statt des Reader-Kastens, damit Safari die Browser-Leiste einklappt.
  // Standard kommt aus der Umgebung (nur dev an, server/index.js); pro Aufruf
  // uebersteuerbar mit `?seitenscroll=1` (an) / `?seitenscroll=0` (aus).
  // SSR und Client lesen dieselben Werte.
  const seitenScrollTest = seitenScrollAn(
    pageContext.urlParsed?.search?.["seitenscroll"],
    (pageContext as { seitenScrollStandard?: boolean }).seitenScrollStandard ===
      true,
  );

  // WESER-KURIER App/Browser-Unterscheidung (2026-08-13): früher `?open`-basiert
  // (data-open des Widgets). Belegt an echtem App-Traffic kommt `?open` real
  // NICHT am Viewer an — die Erkennung läuft jetzt client-seitig in ViewerChrome
  // über isInAppWebView(navigator.userAgent) (WK-App-WKWebView ohne Safari-Token).
  // Kein SSR-Flag mehr nötig.

  // Plan-N2 Block 3 v3 (2026-05-25 Bene): viewportHint kommt aus
  // onRenderHtml (UA-Sniffing) via passToClient. Wird an ViewerChrome
  // weitergereicht, damit useResponsiveViewer's Initial-State auf
  // Mobile bereits "isCompact=true" ist und der weiße Phone-Mockup-
  // Flash beim First-Render entfällt.
  const viewportHint =
    (pageContext as { viewportHint?: "mobile" | "desktop" }).viewportHint ??
    "desktop";
  const initialIsMobile = viewportHint === "mobile";
  // Eingebettet in ein iframe (Server liest Sec-Fetch-Dest): kein Seiten-Scroll,
  // s. seitenScrollNutzbar. SSR und Client sehen denselben Wert.
  const imIframe = (pageContext as { imIframe?: boolean }).imIframe === true;

  // In-App-WebView schon SSR-seitig aus dem Request-User-Agent (passToClient)
  // bestimmen — server + client teilen denselben UA, also rendert der Server
  // das Schliessen-X in der App gar nicht erst → kein X-Blitz nach der Hydration.
  const inAppWebView = isInAppWebView(
    (pageContext as { userAgent?: string }).userAgent,
  );

  // Plan-1.9 Phase 3.B — Open-Graph-Anchor URL-Parsing.
  // Master-Briefing v5 Z. 384-386: `#scene-N` (primär) + `?at=scene-N`
  // (Bot-Fallback). Hash hat Vorrang. 1-indexed in URL — Konvertierung
  // zur 0-indexed Renderer-Position via clampSceneIdxToZeroIndexed.
  const sceneAnchor = parseSceneAnchor(
    pageContext.urlParsed?.hash,
    pageContext.urlParsed?.search,
  );
  const initialSceneIdxOneIndexed = sceneAnchor?.sceneIdxOneIndexed;

  // Sub-Step 3.3 Phase 2 — View-Tracking-Hook auf Page-Level mounten.
  // Triggert genau einen POST /user-scrollys/:pk/view bei 25%-Scroll.
  // Skip wenn renderMode=og (OG-Image-Render).
  // Wichtig: Hook muss VOR den Conditional-Returns gemountet werden, damit
  // Hooks-Order konsistent ist. publicKey ist undefined wenn pageData
  // notFound oder mock-Pfad → Hook macht no-op.
  const resolvedPublicKey =
    pageData?.kind === "success" ? pageData.resolved.publicKey : undefined;
  useViewTracking({
    publicKey: resolvedPublicKey,
    disabled: renderMode === "og" || istVorschau,
  });
  // Abgaben bei VOTE/RATING — nur echte Leser (nicht Vorschau, nicht OG-Bild).
  // STUFE 2 (28.09.2026): an den Server, same-origin wie die View-Zählung
  // (POST /user-scrollys/:publicKey/cta/:ctaId/antwort, Modul
  // src/cta-antworten). Wie dort: abschicken und vergessen — ein Fehler soll
  // den Leser nicht stören, seine Wahl steht ohnehin im Gerät (ctaMerken).
  const ctaAntwort = useCallback(
    (abgabe: CtaAbgabe) => {
      if (!resolvedPublicKey) return;
      void fetch(
        `/user-scrollys/${encodeURIComponent(resolvedPublicKey)}/cta/${encodeURIComponent(abgabe.ctaId)}/antwort`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ art: abgabe.art, antwort: abgabe.antwort, geraet: abgabe.geraet }),
          keepalive: true,
        },
      ).catch(() => {});
    },
    [resolvedPublicKey],
  );
  const onCtaAntwort = renderMode === "og" || istVorschau ? undefined : ctaAntwort;
  // Verweildauer (Heartbeat + sendBeacon) → scrolly_engagements.duration.
  useDurationTracking({
    publicKey: resolvedPublicKey,
    disabled: renderMode === "og" || istVorschau,
  });

  // POST-IDENTITAETS-ANKER (Doppel-Boot-Blitzer, 02.09.2026): Irgendwo
  // oberhalb (Vike-pageContext-Wechsel nach der Hydration) kommt einmal ein
  // NEUES resolved-Objekt mit identischem Inhalt an. Der Viewer vergleicht
  // Post-Identitaeten — ein neues Objekt liess ihn den kompletten
  // Layer-Baum ein zweites Mal aufbauen (sichtbarer Voll-Reload direkt
  // nach dem Boot; per A/B als Alt-Fehler belegt). Hier wird die
  // Identitaet am INHALT verankert: gleicher publicKey + updatedAt =
  // dasselbe post-Objekt wird weitergereicht. Inhalt aendert sich im
  // Reader nur mit updatedAt (Republish) — dann bootet er zu Recht neu.
  const stabilerPostRef = useRef<{ schluessel: string; post: unknown } | null>(
    null,
  );

  // Backend-driven path (primary).
  if (pageData) {
    if (pageData.kind === "success") {
      let resolvedStabil = pageData.resolved;
      const post = resolvedStabil.post as { data?: { updatedAt?: string } };
      const schluessel = `${resolvedStabil.publicKey}|${post?.data?.updatedAt ?? ""}`;
      if (stabilerPostRef.current?.schluessel === schluessel) {
        resolvedStabil = {
          ...resolvedStabil,
          post: stabilerPostRef.current.post as typeof resolvedStabil.post,
        };
      } else {
        stabilerPostRef.current = { schluessel, post: resolvedStabil.post };
      }
      const scrolly = synthScrollyFromResolved(urlKey, resolvedStabil);
      return (
        <BrandingProvider tokens={pageData.resolved.brandingTokens}>
          <ViewerChrome
            scrolly={scrolly}
            renderMode={renderMode}
            chromeMinimal={chromeMinimal}
            onCtaAntwort={onCtaAntwort}
            handyAnsicht={handyAnsicht}
            seitenScrollTest={seitenScrollTest}
            imIframe={imIframe}
            initialSceneIdxOneIndexed={initialSceneIdxOneIndexed}
            publicKey={pageData.resolved.publicKey}
            engagement={pageData.resolved.engagement}
            initialIsMobile={initialIsMobile}
            inAppWebView={inAppWebView}
            /* W2 — Org-weite Cover-Anordnung als Fallback. Die Tokens liegen
               hier ohnehin an (BrandingProvider bekommt sie direkt daneben),
               deshalb eine Prop statt eines neuen Context-Hooks. `null` =
               Org hat nichts gewaehlt → ViewerChrome faellt auf das zurueck,
               was im Scrolly steht. */
            orgCoverTextPosition={
              pageData.resolved.brandingTokens.cover.textPosition
            }
          />
        </BrandingProvider>
      );
    }
    if (pageData.kind === "error") {
      // LoadError renders with platform defaults — no brand-specific styling
      // for the failure screen so the user can recognise it as a system state.
      return (
        <BrandingProvider tokens={BRANDING_DEFAULTS}>
          <LoadError slug={urlKey} />
        </BrandingProvider>
      );
    }
    // pageData.kind === "notFound" falls through to the mock-lookup below.
  }

  // Fallback: mock-only path when +data.ts didn't run or returned notFound,
  // AND the caller opted into mock-fallback via `mockFallbackKey` (only
  // `/s/:slug` does — feed-mocks are slug-keyed).
  if (mockFallbackKey) {
    const mockScrolly = SCROLLYS_BY_SLUG[mockFallbackKey];
    if (mockScrolly) {
      const mockTokens = applyDevTokenOverrides(
        BRANDING_DEFAULTS,
        pageContext.urlParsed?.search,
      );
      return (
        <BrandingProvider tokens={mockTokens}>
          <ViewerChrome
            scrolly={mockScrolly}
            renderMode={renderMode}
            chromeMinimal={chromeMinimal}
            handyAnsicht={handyAnsicht}
            seitenScrollTest={seitenScrollTest}
            imIframe={imIframe}
            initialSceneIdxOneIndexed={initialSceneIdxOneIndexed}
            initialIsMobile={initialIsMobile}
            inAppWebView={inAppWebView}
          />
        </BrandingProvider>
      );
    }
  }

  return (
    <BrandingProvider tokens={BRANDING_DEFAULTS}>
      <NotFound
        ohneRahmen
        eyebrow="Scrolly nicht gefunden"
        headline={
          <>
            Kein Scrolly unter{" "}
            <em style={{ color: PINK, fontStyle: "italic" }}>
              {notFoundUrlPrefix}/{urlKey}
            </em>
            .
          </>
        }
        detail="Der Link ist abgelaufen oder der Scrolly wurde depubliziert."
      />
    </BrandingProvider>
  );
}
